import { Link, useLocation } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import * as React from 'react';

import type { CampaignDetailDto } from '@/api/campaign-client';
import { Badge } from '@/components/ui/badge';
import { campaignStateLabel } from '@/lib/campaign-labels';
import { catalogVersionLabel } from '@/lib/catalog-version-label';
import { CampaignCycleNavigator } from '@/routes/admin/campaigns/campaign-cycle-navigator';

// =============================================================================
// campaign-zone-nav.tsx — Barre de navigation TRANSVERSE de la zone campagne.
// =============================================================================
// Rendue UNE seule fois par le layout de zone (campaign-zone-layout-route), au-dessus de
// l'<Outlet/> : elle est donc IDENTIQUE sur toutes les pages de la zone (détail/hub, prix cibles,
// journal des relances, vue opérationnelle, négociation, analyse, publication, audit).
//
// Trois systèmes de navigation, tous en LIENS TEXTE (hyperliens, plus de boutons) — repère
// graphique GATE_HUMAN (2026-09-08) :
//   1. Barre haute (restyling 2026-09-17, en-tête de zone COMPACT) : fil d'Ariane « ‹ Campagnes / intitulé »
//      → registre des opérations (même lien, nom accessible « Retour à la liste des Campagnes ») à gauche ;
//      navigateur de cycle compact (6 points + « Étape n / 6 ») à droite, porté UNIQUEMENT ici.
//   2. En-tête : <h1> = intitulé de la campagne (serif), badge d'état, « Catalogue figé » + empreinte mono ;
//      mention discrète à droite propre à l'onglet courant (« Vue admin · aucune anonymisation »).
//      Refonte 2026-09-16 : l'intitulé redevient un TITRE <h1> (le titre-lien sans h1 cassait les ST qui
//      localisent la page par son titre) ; le retour au hub passe par l'onglet « Détail ».
//   3. Onglets des vues : « Détail » EN PREMIER (GATE_HUMAN 2026-09-16, Q2 « renomme la page en Détail »),
//      actif sur la route index (hub) ; la page COURANTE est mise en évidence (soulignée accent + gras), les
//      autres sont des liens neutres. L'état actif est dérivé du pathname (robuste vis-à-vis des sous-routes,
//      ex. « operational-view/profils »), pas d'un flag porté par chaque écran.

interface CampaignZoneNavProps {
  readonly campaign: CampaignDetailDto;
}

/** Onglet de vue : `to` = cible de navigation typée ; `seg` = segment de pathname pour l'actif. */
interface ZoneTab {
  readonly to: string;
  /** Segment de pathname ; chaîne vide = route index (hub « Détail »). */
  readonly seg: string;
  readonly label: string;
  /** Mention discrète affichée à droite de l'intitulé quand l'onglet est courant. */
  readonly note?: string;
}

// ORDRE (décision GATE_HUMAN du 2026-09-22, verbatim : « déplacer le tab Négociation, avant
// l'analyse »). Il suit désormais la séquence MÉTIER énoncée au même endroit : on lit la vue
// opérationnelle, on renégocie les TJM qui ne sont pas compétitifs, PUIS on analyse ce qui en résulte,
// avant de publier. Seul l'ORDRE change : aucun libellé, aucune route.
const TABS: readonly ZoneTab[] = [
  { to: '/admin/campaigns/$campaignId', seg: '', label: 'Détail' },
  {
    // P-2 (2026-09-23) — onglet RÉTABLI. L'écran « Suivi de campagne temps réel » porte la RELANCE des
    // fournisseurs sans saisie (modale de composition, envoi). Sa route était enregistrée mais AUCUN
    // lien de l'application n'y menait : on n'y accédait qu'en tapant l'URL. Constat de John en
    // production : « la fonctionnalité de relance fournisseurs n'est plus accessible. » Elle n'avait
    // jamais été supprimée, seulement rendue introuvable.
    // Placé AVANT le journal : on relance ici, on relit là-bas ce qui a été envoyé.
    to: '/admin/campaigns/$campaignId/monitoring',
    seg: 'monitoring',
    label: 'Suivi fournisseurs',
    note: 'Relance des fournisseurs sans saisie',
  },
  { to: '/admin/campaigns/$campaignId/reminders-journal', seg: 'reminders-journal', label: 'Journal des relances' },
  {
    to: '/admin/campaigns/$campaignId/operational-view/profils',
    seg: 'operational-view',
    label: 'Vue opérationnelle',
    // Vue admin NOMINATIVE (pages Profils & Fournisseurs) : mention écrite une seule fois, ici.
    note: 'Vue admin · aucune anonymisation',
  },
  {
    to: '/admin/campaigns/$campaignId/negotiation',
    seg: 'negotiation',
    label: 'Négociation',
    // Refonte N4 : la page ne porte plus de formulaire d'ouverture (décision GATE_HUMAN 2) : elle est
    // le lieu du SUIVI et du VERROUILLAGE. La mention rappelle où s'ouvre une négociation ; elle a
    // suivi le DÉPLACEMENT du 2026-09-22 (elle désignait l'onglet Analyse, qui ne l'offre plus).
    note: 'Négociations ouvertes depuis la vue opérationnelle (Fournisseurs)',
  },
  {
    to: '/admin/campaigns/$campaignId/analysis',
    seg: 'analysis',
    label: 'Analyse',
    // Page FUSIONNÉE (lot U1) : la distribution y est anonymisée, le ranking nominatif en clair.
    // L'onglet « Référencement / Rang » et sa route sont SUPPRIMÉS (ADR-FUSION-ANALYSE-RANG-01,
    // décision 1) : cette page les remplace.
    note: 'Vue admin · ranking en clair, distribution anonymisée',
  },
  { to: '/admin/campaigns/$campaignId/publication', seg: 'publication', label: 'Publication' },
  { to: '/admin/campaigns/$campaignId/audit', seg: 'audit', label: 'Audit' },
];

const TAB_BASE =
  'rounded text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const TAB_INACTIVE = 'font-medium text-muted-foreground hover:text-foreground hover:underline';
const TAB_ACTIVE =
  'font-semibold text-foreground underline decoration-primary decoration-2 underline-offset-8';

export function CampaignZoneNav({
  campaign,
}: CampaignZoneNavProps): React.JSX.Element {
  const state = campaignStateLabel(campaign.state);
  const catalogVersion = catalogVersionLabel(
    campaign.catalogVersionId,
    campaign.catalogVersionNumber,
  );
  const params = { campaignId: campaign.campaignId };
  const { pathname } = useLocation();
  const base = `/admin/campaigns/${campaign.campaignId}`;
  const isActive = (tab: ZoneTab): boolean =>
    tab.seg === ''
      ? pathname === base || pathname === `${base}/`
      : pathname.startsWith(`${base}/${tab.seg}`);
  const note = TABS.find(isActive)?.note;

  return (
    <div className="space-y-4">
      {/* 1. Barre haute : fil d'Ariane « ‹ Campagnes / intitulé » à gauche, progression du cycle à droite. */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <nav
          aria-label="Fil d'Ariane"
          className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground"
        >
          <Link
            to="/admin/campaigns"
            // Nom accessible explicite, contenant le libellé visible « Campagnes » (WCAG 2.5.3).
            aria-label="Retour à la liste des Campagnes"
            // Lien de RETOUR : jamais marqué page courante (la page courante est l'intitulé qui suit).
            activeOptions={{ exact: true }}
            className="inline-flex shrink-0 items-center gap-1 rounded transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
            Campagnes
          </Link>
          <span aria-hidden="true" className="text-muted-foreground/50">
            /
          </span>
          <span aria-current="page" className="truncate text-foreground">
            {campaign.intitule}
          </span>
        </nav>
        <CampaignCycleNavigator state={campaign.state} />
      </div>

      {/* 2. En-tête : intitulé (h1) · état · version de catalogue figée ; mention de vue à droite. */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="font-serif text-3xl font-medium tracking-tight">{campaign.intitule}</h1>
          <Badge
            variant="outline"
            className="border-transparent bg-dash-critical-bg text-dash-critical-fg"
          >
            {state.label}
          </Badge>
          <span className="text-sm text-muted-foreground">
            Catalogue figé{' '}
            {/* P-8 : le NUMÉRO de version quand l'API le résout, l'uuid tronqué sinon (chasse fixe pour
                signaler qu'il s'agit alors d'un identifiant technique, pas d'un nom). L'uuid COMPLET reste
                en infobulle dans les deux cas — c'est lui qu'on donne au support. */}
            <span
              className={catalogVersion.isFallback ? 'font-mono' : 'font-medium text-foreground'}
              title={campaign.catalogVersionId}
            >
              {catalogVersion.text}
            </span>
          </span>
        </div>
        {note !== undefined ? (
          <p className="text-xs text-muted-foreground">{note}</p>
        ) : null}
      </div>

      {/* 3. Onglets des vues : la page courante est mise en évidence (dérivée du pathname). */}
      <nav
        aria-label="Vues de la campagne"
        className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border pb-2"
      >
        {TABS.map((tab) => {
          const active = isActive(tab);
          return (
            <Link
              key={tab.label}
              to={tab.to}
              params={params}
              // Le hub est un préfixe de toutes les vues : sans correspondance exacte, TanStack le marquerait
              // actif (aria-current) sur chaque sous-page.
              activeOptions={{ exact: tab.seg === '' }}
              aria-current={active ? 'page' : undefined}
              className={`${TAB_BASE} ${active ? TAB_ACTIVE : TAB_INACTIVE}`}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
